/**
 * Locate the materialized "Tables" folder nodes for a given (connection,
 * database) in the Object Explorer tree.
 *
 * Needed after a clone: the new table has to appear without the user hitting
 * Refresh. A node id can't just be computed, because the schema-grouped layout
 * puts a schema container above each object-type folder — so the id carries a
 * schema segment there and doesn't in the flat layout, and a database with many
 * schemas has several Tables folders. Walking the tree finds whichever ones the
 * user actually has open.
 */

import type { FolderNodeBaseMetadata } from '../../explorer/models/DatabaseTreeModels';

/** The only shape this walker needs — every tree node satisfies it. */
type WalkableNode = {
  id: string;
  type: string;
  metadata?: unknown;
  children?: WalkableNode[];
};

export function findTablesFolderNodeIds(
  treeData: readonly WalkableNode[],
  connectionId: string,
  database: string,
): string[] {
  const found: string[] = [];

  const walk = (nodes: readonly WalkableNode[] | undefined): void => {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'tables-folder') {
        const metadata = node.metadata as FolderNodeBaseMetadata | undefined;
        if (metadata?.connectionId === connectionId && metadata?.database === database) {
          found.push(node.id);
        }
      }
      walk(node.children);
    }
  };

  walk(treeData);
  return found;
}
